import { useState, useEffect } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import { navItems } from '../data/siteData';

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function LogoMark() {
  return (
    <img src="/logo.svg" alt="Klar" className="logo-img" />
  );
}

function Layout({ theme, onToggleTheme, currentYear }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const footerLinks = {
    Product: [
      { label: 'Features', to: '/product' },
      { label: 'Student Journey', to: '/student-journey' },
      { label: 'Download', to: '/contact' },
    ],
    Legal: [
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'Terms & Conditions', to: '/terms' },
    ],
    Connect: [
      { label: 'Contact Us', to: '/contact' },
      { label: 'Email Us', href: 'mailto:affan@equios.co' },
    ],
  };

  return (
    <>
      {/* ── Topbar ────────────────────────────────────────── */}
      <header className={`topbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="topbar-inner">
            {/* Logo */}
            <Link to="/" className="topbar-logo">
              <LogoMark />
              <span className="logo-text">Klar</span>
            </Link>

            {/* Desktop nav */}
            <nav className="nav-links" aria-label="Main navigation">
              {navItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* Actions */}
            <div className="nav-actions">
              <button
                className="theme-toggle"
                onClick={onToggleTheme}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              </button>
              <Link to="/contact" className="btn btn-primary btn-sm">
                Get the App
              </Link>
              <button
                className={`hamburger ${menuOpen ? 'open' : ''}`}
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Toggle menu"
                aria-expanded={menuOpen}
              >
                <span className="hamburger-line" />
                <span className="hamburger-line" />
                <span className="hamburger-line" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile menu ───────────────────────────────────── */}
      <nav className={`mobile-menu ${menuOpen ? 'open' : ''}`} aria-label="Mobile navigation">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`}
          >
            {item.label}
          </NavLink>
        ))}
        <div className="mobile-menu-cta">
          <Link to="/contact" className="btn btn-primary w-full" style={{ justifyContent: 'center' }}>
            Get the App — It's Free
          </Link>
        </div>
      </nav>

      {/* ── Page content ──────────────────────────────────── */}
      <main>
        <Outlet />
      </main>

      {/* ── Footer ────────────────────────────────────────── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            {/* Brand column */}
            <div className="footer-brand">
              <Link to="/" className="topbar-logo" style={{ display: 'inline-flex', marginBottom: '0' }}>
                <LogoMark />
                <span className="logo-text">Klar</span>
              </Link>
              <p className="footer-tagline">
                The no-nonsense budget tracker built for Indian college students. Know where every rupee goes — for free, forever.
              </p>
              <div className="footer-badge">
                <span className="pill-dot-pulse" />
                Live on the App Store &amp; Google Play
              </div>
            </div>

            {/* Link columns */}
            {Object.entries(footerLinks).map(([title, links]) => (
              <div key={title}>
                <p className="footer-col-title">{title}</p>
                <ul className="footer-links">
                  {links.map(link => (
                    <li key={link.label}>
                      {link.href ? (
                        <a href={link.href} className="footer-link">{link.label}</a>
                      ) : (
                        <Link to={link.to} className="footer-link">{link.label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="footer-bottom">
            <p className="footer-copy">
              © {currentYear} Klar by Equios. All rights reserved.
            </p>
            <p className="footer-made">
              Made with&nbsp;<span style={{ color: 'var(--red)' }}>♥</span>&nbsp;in India for students
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Layout;
